import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { ParentSize } from '@visx/responsive';
import { scaleTime, scaleLinear } from '@visx/scale';
import { Group } from '@visx/group';
import { LinePath } from '@visx/shape';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { GridColumns } from '@visx/grid';
import { TooltipWithBounds, useTooltip, defaultStyles } from '@visx/tooltip';
import { localPoint } from '@visx/event';
import { bisector } from 'd3-array';
import dayjs from 'dayjs';

import { calculateXAxisTickValues, tickFormatFactory } from '../../utils/chartHelpers';
import { DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE } from '../../utils/dates';
import { convertGramsToLargeUnits, convertLargeUnitsToGrams, weightLargeUnitLabel } from '../../utils/unitConversion';
import './BinSetInventoryChart.scss';

const TOOLTIP_STYLES = {
  ...defaultStyles,
  borderRadius: '8px',
  backgroundColor: 'rgba(40, 40, 40, 0.8)',
  color: 'white',
  border: '1px solid #E6E6E6',
  padding: '1rem',
};

function zoomEndpointToUnixSeconds(v) {
  if (v == null) return null;
  if (v instanceof Date) return Math.floor(v.getTime() / 1000);
  if (typeof v === 'number') {
    return v > 1e12 ? Math.floor(v / 1000) : v;
  }
  return null;
}

function canonicalTimeUnixSec(t) {
  if (t == null || !Number.isFinite(t)) return 0;
  return t > 1e12 ? Math.floor(t / 1000) : Math.floor(t);
}

// Data accessors for {t, v} format where t = unix seconds (or epoch ms if > 1e12), v = value
export const getX = (point) => point.t;
export const getY = (point) => point.v;

/** @param {number} t unix seconds or epoch ms */
export function timeValueToDate(t) {
  if (t == null || !Number.isFinite(t)) return new Date(0);
  return new Date(t > 1e12 ? t : t * 1000);
}

/** Marker circle radius (was 5; 2× for visibility). */
const EVENT_MARKER_RADIUS = 10;
/** Pixels from marker center for tooltip hit-test. */
const EVENT_MARKER_HIT_RADIUS = 26;
/** Gap between inventory line and bottom of marker disk (marker sits just above the line). */
const EVENT_MARKER_GAP_ABOVE_LINE_PX = 10;
/** Must match single-line `.BinSetInventoryChart-headingRow` height + margin (fillParentHeight SVG sizing). */
const CHART_HEADING_RESERVE_PX = 30;
/** Padding above/below the content Y range (10% of span). */
const Y_AXIS_BUFFER_RATIO = 0.1;
/** Horizontal reference line at zero inventory. */
const ZERO_REFERENCE_LINE_STROKE = '#999999';

/**
 * Value on the inventory polyline at time `t` (linear segments between points; matches {@link LinePath}).
 * @param {Array<{ t: number, v: number }>} series ascending by `t`
 * @param {number} tUnix unix seconds or ms
 * @returns {number} grams (0 if empty / non-finite)
 */
export function interpolateTimeSeriesAtT(series, tUnix) {
  if (!series?.length) return 0;
  const tCanon = canonicalTimeUnixSec(tUnix);
  const bisectT = bisector((d) => canonicalTimeUnixSec(d.t)).left;
  const i = bisectT(series, tCanon);
  if (i <= 0) return Number.isFinite(series[0].v) ? series[0].v : 0;
  if (i >= series.length) {
    const last = series[series.length - 1];
    return Number.isFinite(last.v) ? last.v : 0;
  }
  const p0 = series[i - 1];
  const p1 = series[i];
  const t0 = canonicalTimeUnixSec(p0.t);
  const t1 = canonicalTimeUnixSec(p1.t);
  if (t1 === t0) return Number.isFinite(p1.v) ? p1.v : 0;
  const frac = (tCanon - t0) / (t1 - t0);
  const v = p0.v + frac * (p1.v - p0.v);
  return Number.isFinite(v) ? v : 0;
}

/**
 * When the earliest sample is after `domainStart`, prepend a point at the chart start using that
 * sample's value so the line reaches the left edge instead of beginning mid-chart.
 *
 * @param {Array<{ t: number, v: number }>} series
 * @param {number} domainStart unix seconds or ms
 * @returns {Array<{ t: number, v: number }>}
 */
export function extendSeriesToDomainStart(series, domainStart) {
  if (!series?.length || domainStart == null || !Number.isFinite(domainStart)) {
    return series ?? [];
  }
  const startT = canonicalTimeUnixSec(domainStart);
  let earliest = series[0];
  let earliestT = canonicalTimeUnixSec(earliest.t);
  for (const point of series) {
    const t = canonicalTimeUnixSec(point.t);
    if (t < earliestT) {
      earliestT = t;
      earliest = point;
    }
  }
  if (earliestT <= startT) return series;
  const v = Number.isFinite(earliest.v) ? earliest.v : 0;
  return [{ t: startT, v }, ...series];
}

export function getEventMarkerKey(m) {
  return m.faultId || `${m.t}-${m.kind}`;
}

/**
 * Keep event marker disk fully inside the plot area; return null when entirely off-screen horizontally.
 *
 * @param {number} xPos plot X of marker center (from xScale)
 * @param {number} cyPlot plot Y of marker center
 * @param {number} plotWidth xMax
 * @param {number} plotHeight yMax
 * @returns {{ cx: number, cy: number } | null}
 */
export function clampEventMarkerPlotPosition(xPos, cyPlot, plotWidth, plotHeight) {
  if (!Number.isFinite(xPos) || !Number.isFinite(cyPlot)) return null;
  const r = EVENT_MARKER_RADIUS;
  if (xPos < -r || xPos > plotWidth + r) return null;
  const min = r;
  const maxX = plotWidth - r;
  const maxY = plotHeight - r;
  if (maxX < min || maxY < min) return null;
  return {
    cx: Math.min(maxX, Math.max(min, xPos)),
    cy: Math.min(maxY, Math.max(min, cyPlot)),
  };
}

/** White play-triangle glyph (Open), 20×20, centered on marker. */
export function EventMarkerOpenIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden className="BinSetInventoryChart-eventMarkerIcon">
      <path d="M6 4.5 L15.5 10 L6 15.5 Z" fill="#fff" />
    </svg>
  );
}

/** White pause-bars glyph (Close), 20×20, centered on marker. */
export function EventMarkerCloseIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden className="BinSetInventoryChart-eventMarkerIcon">
      <rect x="6" y="4.5" width="2.6" height="11" rx="0.6" fill="#fff" />
      <rect x="11.4" y="4.5" width="2.6" height="11" rx="0.6" fill="#fff" />
    </svg>
  );
}

/** White vibration bars glyph, 20×20, centered on marker. */
export function EventMarkerVibrationIcon() {
  const bars = [
    { x: 4, h: 6 },
    { x: 7, h: 10 },
    { x: 10, h: 14 },
    { x: 13, h: 10 },
    { x: 16, h: 6 },
  ];
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden className="BinSetInventoryChart-eventMarkerIcon">
      {bars.map(({ x, h }) => (
        <rect key={x} x={x - 1} y={10 - h / 2} width="2" height={h} rx="1" fill="#fff" />
      ))}
    </svg>
  );
}

/** Glyph for an event marker by `kind`. */
function EventMarkerGlyphForKind({ kind }) {
  if (kind === 'open') return <EventMarkerOpenIcon />;
  if (kind === 'close') return <EventMarkerCloseIcon />;
  return <EventMarkerVibrationIcon />;
}

EventMarkerGlyphForKind.propTypes = {
  kind: PropTypes.oneOf(['open', 'close', 'vibration']).isRequired,
};

function BinSetInventoryChart({
  step_bin_level_history = [],
  projection_past_deliveries = [],
  projection_with_scheduled = [],
  projection_with_recommended = [],
  /** When false, only past inventory (+ optional truth) is drawn; legend and tooltips omit delivery/schedule projections. */
  showProjectionLines = true,
  isMetric = false,
  chartHeight = 300,
  chartMargin = { top: 20, bottom: 25, left: 50, right: 25 },
  chartStartTime = 0,
  chartEndTime = 0,
  chartBackgroundBands = [],
  eventMarkers = [],
  // Truth data props
  knownBinLevelData = [],
  displayTruthData = false,
  // Bin capacity prop
  totalBinCapacity = 0,
  /** Optional title shown above the chart (e.g. per-bin stack). */
  chartHeading = '',
  /** Optional control(s) aligned with the heading (e.g. solo-overlay eye). */
  chartHeaderAccessory = null,
  /** Use parent container height for the SVG (flex layout); set chartHeight as fallback minimum when height is still 0. */
  fillParentHeight = false,
  /** When false, hide the overlay legend for line series (Inventory / projections / truth). */
  showLineLegend = true,
  /** Synchronized X domain across charts (unix seconds); omit for independent zoom per chart. */
  sharedZoomDomain = null,
  /** @param {{ start: number, end: number }} domain unix seconds */
  onZoomDomainChange = null,
  /** When true, omit the bottom axis (used in stacked per-bin charts that share one x-axis). */
  hideXAxis = false,
}) {
  const { tooltipData, tooltipLeft = 0, tooltipTop = 0, tooltipOpen, showTooltip, hideTooltip } = useTooltip();
  const [hoveredEventMarkerKey, setHoveredEventMarkerKey] = useState(null);
  const [zoomStartPoint, setZoomStartPoint] = useState(null);
  const [zoomEndPoint, setZoomEndPoint] = useState(null);

  const clipPathId = useMemo(
    () => `binInventoryChartBoundary-${Math.random().toString(36).slice(2, 11)}`,
    [],
  );

  const resolvedChartMargin = useMemo(() => {
    const m = { ...chartMargin };
    if (isMetric) {
      m.left += 15;
    }
    return m;
  }, [chartMargin, isMetric]);
  if (hideXAxis) {
    chartMargin = { ...chartMargin, bottom: 8 };
  }
  // Convert known bin level data to the expected format
  const truthDataSeries = useMemo(() => {
    if (!displayTruthData || !knownBinLevelData.length) {
      return [];
    }

    return knownBinLevelData.map((kbl) => ({
      t: kbl.valid_at,
      v: kbl.level_in_grams,
    }));
  }, [knownBinLevelData, displayTruthData]);

  /**
   * Full X domain from props or series (unix seconds); resets when data/props change unless a shared zoom is active.
   *
   * @type {{ start: number, end: number }}
   */
  const inferredDomain = useMemo(() => {
    if (chartStartTime && chartEndTime) {
      return { start: chartStartTime, end: chartEndTime };
    }

    const allData = [
      ...step_bin_level_history,
      ...(showProjectionLines
        ? [
          ...projection_past_deliveries,
          ...projection_with_scheduled,
          ...projection_with_recommended,
        ]
        : []),
      ...truthDataSeries,
    ];

    if (allData.length === 0) {
      return { start: dayjs.tz().unix(), end: dayjs.tz().add(1, 'day').unix() };
    }

    const timestamps = allData.map((d) => d.t);
    const minTimestamp = Math.min(...timestamps);
    const maxTimestamp = Math.max(...timestamps);

    return {
      start: minTimestamp,
      end: maxTimestamp,
    };
  }, [
    step_bin_level_history,
    projection_past_deliveries,
    projection_with_scheduled,
    projection_with_recommended,
    truthDataSeries,
    chartStartTime,
    chartEndTime,
    showProjectionLines,
  ]);

  const baseStart = inferredDomain.start;
  const baseEnd = inferredDomain.end;

  const [internalStart, setInternalStart] = useState(baseStart);
  const [internalEnd, setInternalEnd] = useState(baseEnd);

  useEffect(() => {
    if (!sharedZoomDomain) {
      setInternalStart(baseStart);
      setInternalEnd(baseEnd);
    }
  }, [baseStart, baseEnd, sharedZoomDomain]);

  const sharedStart =
    sharedZoomDomain?.start != null ? zoomEndpointToUnixSeconds(sharedZoomDomain.start) : null;
  const sharedEnd =
    sharedZoomDomain?.end != null ? zoomEndpointToUnixSeconds(sharedZoomDomain.end) : null;

  const viewStart = sharedStart ?? internalStart;
  const viewEnd = sharedEnd ?? internalEnd;

  // Prepare chart lines
  const chartLines = useMemo(() => {
    const inventoryLine = {
      linePlot: step_bin_level_history,
      label: 'Inventory',
      colour: '#26af5f',
      legend: true,
    };
    const projectionLines = showProjectionLines
      ? [
        {
          linePlot: projection_past_deliveries,
          label: 'As Delivered',
          colour: '#26af5f',
          strokeDasharray: '5,5',
          legend: false,
          isProjection: true,
        },
        {
          linePlot: projection_with_scheduled,
          label: 'As Scheduled',
          colour: '#0ba5ec',
          strokeDasharray: '5,5',
          legend: true,
          isProjection: true,
        },
        {
          linePlot: projection_with_recommended,
          label: 'As Recommended',
          colour: '#ddbaf3',
          strokeDasharray: '5,5',
          legend: true,
          isProjection: true,
        },
      ]
      : [];
    const truthLine =
      displayTruthData && truthDataSeries.length > 0
        ? [
          {
            linePlot: truthDataSeries,
            label: 'Truth Data',
            colour: '#65C9DA', // Use existing color palette truthData color
            legend: true,
          },
        ]
        : [];
    return [inventoryLine, ...projectionLines, ...truthLine].map((line) => ({
      ...line,
      linePlot: line.isProjection ? line.linePlot : extendSeriesToDomainStart(line.linePlot, viewStart),
    }));
  }, [
    step_bin_level_history,
    projection_past_deliveries,
    projection_with_scheduled,
    projection_with_recommended,
    truthDataSeries,
    displayTruthData,
    showProjectionLines,
    viewStart,
  ]);

  const eventMarkersForPaint = useMemo(() => {
    if (!eventMarkers?.length || !hoveredEventMarkerKey) return eventMarkers;
    return [...eventMarkers].sort((a, b) => {
      const ka = getEventMarkerKey(a);
      const kb = getEventMarkerKey(b);
      if (ka === hoveredEventMarkerKey) return 1;
      if (kb === hoveredEventMarkerKey) return -1;
      return 0;
    });
  }, [eventMarkers, hoveredEventMarkerKey]);

  const handleChartMouseLeave = useCallback(() => {
    setHoveredEventMarkerKey(null);
    setZoomStartPoint(null);
    setZoomEndPoint(null);
    hideTooltip();
  }, [hideTooltip]);

  return (
    <ParentSize
      debounceTime={10}
      className={fillParentHeight ? 'BinSetInventoryChart-parentSizer' : undefined}
      parentSizeStyles={
        fillParentHeight
          ? { width: '100%', height: '100%', flex: '1 1 0', minHeight: 0, display: 'flex', flexDirection: 'column' }
          : undefined
      }
    >
      {(parent) => {
        const headingReserve = chartHeading || chartHeaderAccessory ? CHART_HEADING_RESERVE_PX : 0;
        const plotHeight = fillParentHeight
          ? Math.max(chartHeight, parent.height - headingReserve)
          : chartHeight;

        const xMax = parent.width - resolvedChartMargin.left - resolvedChartMargin.right;
        const yMax = plotHeight - resolvedChartMargin.top - resolvedChartMargin.bottom;

        const xAxisTicks = calculateXAxisTickValues(viewStart, viewEnd);
        const rawXAxisTickFormat = tickFormatFactory(viewStart, viewEnd);
        const xAxisTickFormat = (value, index) => {
          const sec = value instanceof Date ? Math.floor(value.getTime() / 1000) : value;
          return rawXAxisTickFormat(sec, index);
        };

        const xScale = scaleTime({
          domain: [timeValueToDate(viewStart), timeValueToDate(viewEnd)],
          range: [0, xMax],
        });

        const nonProjectionYValues = chartLines.flatMap((line) =>
          !line.isProjection && line.linePlot?.length ? line.linePlot.map(getY) : [],
        );
        const allYValues = chartLines.flatMap((line) =>
          line.linePlot?.length ? line.linePlot.map(getY) : [],
        );
        const dataMinY = nonProjectionYValues.length ? Math.min(...nonProjectionYValues) : 0;
        const dataMaxY = allYValues.length ? Math.max(...allYValues) : 0;
        const rangeMin = dataMinY < 0 ? dataMinY : 0;
        const rangeMax =
          totalBinCapacity > 0 ? Math.max(totalBinCapacity, dataMaxY) : Math.max(0, dataMaxY);
        const span = Math.max(rangeMax - rangeMin, 1);
        const yBuffer = span * Y_AXIS_BUFFER_RATIO;
        const minYValue = dataMinY < 0 ? rangeMin - yBuffer : 0;
        const maxYValue = rangeMax + yBuffer;

        const yScale = scaleLinear({
          domain: [minYValue, maxYValue],
          range: [yMax, 0],
        });

        // Y-axis ticks are formatted at 0 decimal places (whole large units), so do not produce more
        // ticks than there are whole numbers in the visible large-unit range — otherwise neighboring
        // ticks display the same rounded label (e.g. 0, 0, 1, 1, 2).
        const maxLargeUnits = convertGramsToLargeUnits(isMetric, maxYValue, null);
        const minLargeUnits = convertGramsToLargeUnits(isMetric, minYValue, null);
        const floorMaxLargeUnits = Math.floor(maxLargeUnits);
        const ceilMinLargeUnits = Math.ceil(minLargeUnits);
        const wholeTickCount = Math.max(1, floorMaxLargeUnits - ceilMinLargeUnits + 1);
        const desiredYTickCount = Math.min(5, wholeTickCount);
        const rangeLargeUnits = Math.max(0, floorMaxLargeUnits - ceilMinLargeUnits);
        const yTickStepLargeUnits = Math.max(
          1,
          Math.ceil(rangeLargeUnits / Math.max(1, desiredYTickCount - 1)),
        );
        const yAxisTickValues = [];
        for (let v = ceilMinLargeUnits; v <= floorMaxLargeUnits; v += yTickStepLargeUnits) {
          yAxisTickValues.push(convertLargeUnitsToGrams(isMetric, v, null));
        }
        if (yAxisTickValues.length === 0) yAxisTickValues.push(minYValue);

        const formatDateTooltip = (timestampUnixSec) => {
          return dayjs.unix(timestampUnixSec).format(DATE_TIME_FORMAT_MONTH_DAY_HOUR_MINUTE);
        };

        const getEventMarkerPlotPosition = (m) => {
          const xPos = xScale(timeValueToDate(m.t));
          const vGrams = interpolateTimeSeriesAtT(step_bin_level_history, m.t);
          const yLine = yScale(vGrams);
          const idealCy = yLine - EVENT_MARKER_RADIUS - EVENT_MARKER_GAP_ABOVE_LINE_PX;
          return clampEventMarkerPlotPosition(xPos, idealCy, xMax, yMax);
        };

        const handleTooltip = (event) => {
          const { x, y } = localPoint(event) || { x: 0, y: 0 };

          if (eventMarkers?.length > 0) {
            const hitR2 = EVENT_MARKER_HIT_RADIUS * EVENT_MARKER_HIT_RADIUS;
            let closest = null;
            let closestD2 = hitR2;
            for (const m of eventMarkers) {
              const plotPos = getEventMarkerPlotPosition(m);
              if (!plotPos) continue;
              const cx = resolvedChartMargin.left + plotPos.cx;
              const cy = resolvedChartMargin.top + plotPos.cy;
              const dx = x - cx;
              const dy = y - cy;
              const d2 = dx * dx + dy * dy;
              if (d2 <= hitR2 && d2 < closestD2) {
                closestD2 = d2;
                closest = m;
              }
            }
            if (closest) {
              const ts = Number(closest.t);
              const key = getEventMarkerKey(closest);
              setHoveredEventMarkerKey((prev) => (prev === key ? prev : key));
              const headingReserve = chartHeading || chartHeaderAccessory ? CHART_HEADING_RESERVE_PX : 0;
              const closestPlotPos = getEventMarkerPlotPosition(closest);
              showTooltip({
                tooltipData: {
                  mode: 'event',
                  event: closest,
                  displayedDate: formatDateTooltip(canonicalTimeUnixSec(ts)),
                },
                tooltipLeft: closestPlotPos ? closestPlotPos.cx : x - resolvedChartMargin.left,
                tooltipTop:
                  headingReserve +
                  resolvedChartMargin.top +
                  (closestPlotPos ? closestPlotPos.cy : 0),
              });
              return;
            }
          }

          setHoveredEventMarkerKey((prev) => (prev == null ? prev : null));

          const x0 = xScale.invert(x - resolvedChartMargin.left);
          const x0Unix = Math.floor(x0.getTime() / 1000);
          // Find the closest point for each line
          let lineIntercepts = chartLines
            .map(({ linePlot, label, colour }) => {
              if (linePlot.length === 0) return null;

              const bisectTime = bisector((point) => canonicalTimeUnixSec(point.t)).center;

              const index = bisectTime(linePlot, x0Unix);
              const point = linePlot[index];

              if (!point) return null;
              if (Math.abs(canonicalTimeUnixSec(point.t) - x0Unix) > 60 * 60 * 6) return null;

              return {
                label,
                value: point.v,
                timestamp: point.t,
                colour,
              };
            })
            .filter(Boolean);

          lineIntercepts = lineIntercepts.filter(
            ({ label }) =>
              label !== 'As Delivered' || !lineIntercepts.some(({ label: l }) => l === 'Inventory'),
          );

          if (lineIntercepts.length > 0) {
            const formattedDate = formatDateTooltip(x0Unix);

            showTooltip({
              tooltipData: {
                mode: 'lines',
                lineIntercepts,
                displayedDate: formattedDate,
              },
              tooltipLeft: x - resolvedChartMargin.left,
              tooltipTop: 0,
            });
          }
        };

        const onSvgMouseMove = (event) => {
          handleTooltip(event);
          if (zoomStartPoint != null) {
            setZoomEndPoint(localPoint(event).x);
          }
        };

        const onSvgMouseDown = (event) => {
          const xDown = localPoint(event).x;
          setZoomStartPoint(xDown);
          setZoomEndPoint(xDown);
        };

        const onSvgMouseUp = () => {
          const leftM = resolvedChartMargin.left;
          const startPoint = zoomStartPoint > zoomEndPoint ? zoomEndPoint : zoomStartPoint;
          const endPoint = zoomStartPoint <= zoomEndPoint ? zoomEndPoint : zoomStartPoint;

          if (
            startPoint != null &&
            endPoint != null &&
            endPoint - startPoint > 20
          ) {
            const plotStart = Math.max(0, Math.min(xMax, startPoint - leftM));
            const plotEnd = Math.max(0, Math.min(xMax, endPoint - leftM));
            const d0 = xScale.invert(plotStart);
            const d1 = xScale.invert(plotEnd);
            let ns0 = Math.floor(d0.getTime() / 1000);
            let ns1 = Math.floor(d1.getTime() / 1000);
            if (ns0 > ns1) {
              const t = ns0;
              ns0 = ns1;
              ns1 = t;
            }

            if (onZoomDomainChange) {
              onZoomDomainChange({ start: ns0, end: ns1 });
            } else {
              setInternalStart(ns0);
              setInternalEnd(ns1);
            }
          }

          setZoomStartPoint(null);
          setZoomEndPoint(null);
        };

        const onSvgDoubleClick = () => {
          if (onZoomDomainChange) {
            onZoomDomainChange({ start: baseStart, end: baseEnd });
          } else {
            setInternalStart(baseStart);
            setInternalEnd(baseEnd);
          }
        };

        const svgCursor = zoomStartPoint != null && zoomEndPoint != null ? 'zoom-in' : 'crosshair';

        return (
          <div
            className={`BinSetInventoryChart${fillParentHeight ? ' BinSetInventoryChart--fillParent' : ''}`}
          >
            {chartHeading || chartHeaderAccessory ? (
              <div className="BinSetInventoryChart-headingRow">
                {chartHeading ? <span className="BinSetInventoryChart-heading">{chartHeading}</span> : null}
                {chartHeaderAccessory ? (
                  <div className="BinSetInventoryChart-headingAccessory">{chartHeaderAccessory}</div>
                ) : null}
              </div>
            ) : null}
            <svg
              width={parent.width}
              height={plotHeight}
              style={{ userSelect: 'none', cursor: svgCursor }}
              onMouseMove={onSvgMouseMove}
              onMouseDown={onSvgMouseDown}
              onMouseUp={onSvgMouseUp}
              onMouseLeave={handleChartMouseLeave}
              onDoubleClick={onSvgDoubleClick}
            >
              <Group left={resolvedChartMargin.left} top={resolvedChartMargin.top}>
                <defs>
                  <clipPath id={clipPathId}>
                    <rect x={0} y={0} width={xMax} height={yMax} />
                  </clipPath>
                </defs>
                <g clipPath={`url(#${clipPathId})`}>
                  {chartBackgroundBands.map((band, bi) => {
                    const x1 = xScale(timeValueToDate(band.tStart));
                    const x2 = xScale(timeValueToDate(band.tEnd));
                    if (!Number.isFinite(x1) || !Number.isFinite(x2)) return null;
                    const w = Math.max(0, x2 - x1);
                    return (
                      <rect
                        key={`band-${bi}`}
                        x={x1}
                        y={0}
                        width={w}
                        height={yMax}
                        fill={band.color}
                        opacity={band.opacity ?? 0.2}
                      >
                        <title>{band.label}</title>
                      </rect>
                    );
                  })}
                </g>
                <GridColumns
                  scale={xScale}
                  width={xMax}
                  height={yMax}
                  stroke="#D9DCE1"
                  tickValues={xAxisTicks.slice(1).map((t) => timeValueToDate(t))}
                />
                {minYValue <= 0 && maxYValue >= 0 ? (
                  <line
                    x1={0}
                    x2={xMax}
                    y1={yScale(0)}
                    y2={yScale(0)}
                    stroke={ZERO_REFERENCE_LINE_STROKE}
                    strokeWidth={1}
                    clipPath={`url(#${clipPathId})`}
                    pointerEvents="none"
                  />
                ) : null}
                {!hideXAxis && (
                  <AxisBottom
                    top={yMax}
                    scale={xScale}
                    hideTicks
                    stroke="#D9DCE1"
                    tickFormat={xAxisTickFormat}
                    tickValues={xAxisTicks.map((t) => timeValueToDate(t))}
                  />
                )}
                <AxisLeft
                  scale={yScale}
                  hideTicks
                  stroke="#D9DCE1"
                  tickValues={yAxisTickValues}
                  tickFormat={(value) =>
                    `${convertGramsToLargeUnits(isMetric, value, 0)} ${weightLargeUnitLabel(isMetric)}`
                  }
                  tickLabelProps={() => ({
                    dx: isMetric ? -45 : -30,
                  })}
                />

                {chartLines.toReversed().map(({ linePlot, colour, strokeDasharray }, index) => {
                  return (
                    <LinePath
                      key={index}
                      data={linePlot}
                      x={(d) => xScale(timeValueToDate(getX(d)))}
                      y={(d) => yScale(getY(d))}
                      stroke={colour}
                      strokeWidth={2}
                      strokeDasharray={strokeDasharray}
                      clipPath={`url(#${clipPathId})`}
                    />
                  );
                })}

                {eventMarkersForPaint.map((m) => {
                  const plotPos = getEventMarkerPlotPosition(m);
                  if (!plotPos) return null;
                  const fill = m.markerFill || '#64748b';
                  const markerKey = getEventMarkerKey(m);
                  const isHovered = markerKey === hoveredEventMarkerKey;
                  return (
                    <g
                      key={markerKey}
                      transform={`translate(${plotPos.cx - EVENT_MARKER_RADIUS}, ${plotPos.cy - EVENT_MARKER_RADIUS})`}
                    >
                      <circle
                        className={
                          isHovered
                            ? 'BinSetInventoryChart-eventMarkerCircle BinSetInventoryChart-eventMarkerCircle--hovered'
                            : 'BinSetInventoryChart-eventMarkerCircle'
                        }
                        cx={EVENT_MARKER_RADIUS}
                        cy={EVENT_MARKER_RADIUS}
                        r={EVENT_MARKER_RADIUS}
                        fill={fill}
                      />
                      <g transform={`translate(${EVENT_MARKER_RADIUS - 10}, ${EVENT_MARKER_RADIUS - 10})`}>
                        <EventMarkerGlyphForKind kind={m.kind} />
                      </g>
                    </g>
                  );
                })}

                {
                  // Vertical crosshair + line intercept markers (inventory tooltip only).
                  tooltipOpen && tooltipData?.mode !== 'event' && (
                    <g pointerEvents="none">
                      <line
                        stroke="#000"
                        strokeWidth="1"
                        x1={tooltipLeft}
                        x2={tooltipLeft}
                        y1={0}
                        y2={yMax}
                        strokeDasharray="5,5"
                      />
                      {
                        // Place circular intercept markers for all visible chart lines.
                        tooltipData.lineIntercepts.toReversed().map(({ value, timestamp, colour }, index) => {
                          const x = xScale(timeValueToDate(timestamp));
                          const y = yScale(value);
                          if (!Number.isFinite(x) || !Number.isFinite(y)) {
                            return null;
                          }
                          return (
                            <circle
                              key={index}
                              className="BinSetInventoryChart-chartInterceptCircle"
                              cx={x}
                              cy={y}
                              r={7}
                              stroke={colour}
                              strokeWidth="2.5"
                              fill="white"
                            />
                          );
                        })}
                    </g>
                  )
                }
              </Group>
              {zoomStartPoint != null && zoomEndPoint != null ? (
                <rect
                  x={Math.min(zoomStartPoint, zoomEndPoint)}
                  y={chartMargin.top ?? 20}
                  width={Math.abs(zoomEndPoint - zoomStartPoint)}
                  height={plotHeight - (chartMargin.top ?? 20)}
                  fill="#00000022"
                  pointerEvents="none"
                />
              ) : null}
            </svg>

            {showLineLegend && (
              <div
                className="BinSetInventoryChart-legend"
                style={{ top: resolvedChartMargin.top, left: resolvedChartMargin.left }}
              >
                {chartLines
                  .filter(({ legend }) => legend)
                  .map(({ label, colour }, index) => {
                    return (
                      <div className="legendItem" key={index}>
                        <div className="left">
                          <div className="circle" style={{ backgroundColor: colour }} />
                          <span>{label}</span>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}

            {tooltipOpen && tooltipData && (
              <TooltipWithBounds
                key={Math.random()}
                top={tooltipTop}
                left={tooltipLeft + 35 + resolvedChartMargin.left}
                style={TOOLTIP_STYLES}
              >
                {tooltipData.mode === 'event' ? (
                  <div className="BinSetInventoryChart-tooltipInfo BinSetInventoryChart-tooltipInfo--event">
                    <p className="BinSetInventoryChart-tooltipInfoHeader">{tooltipData.displayedDate}</p>
                    <dl className="BinSetInventoryChart-tooltipEventAxes">
                      <div className="BinSetInventoryChart-tooltipEventAxisRow">
                        <dt className="BinSetInventoryChart-tooltipEventAxisTerm">Actions</dt>
                        <dd className="BinSetInventoryChart-tooltipEventAxisDef">
                          {tooltipData.event.interactionLabel ?? (
                            tooltipData.event.kind === 'open'
                              ? 'Open'
                              : tooltipData.event.kind === 'close'
                                ? 'Close'
                                : 'Vibration'
                          )}
                        </dd>
                      </div>
                      <div className="BinSetInventoryChart-tooltipEventAxisRow">
                        <dt className="BinSetInventoryChart-tooltipEventAxisTerm">Source</dt>
                        <dd className="BinSetInventoryChart-tooltipEventAxisDef">
                          {tooltipData.event.source ?? tooltipData.event.label}
                        </dd>
                      </div>
                    </dl>
                    {tooltipData.event.detail ? (
                      <p className="BinSetInventoryChart-tooltipEventDetail">{tooltipData.event.detail}</p>
                    ) : null}
                  </div>
                ) : (
                  <div className="BinSetInventoryChart-tooltipInfo">
                    <p className="BinSetInventoryChart-tooltipInfoHeader">{tooltipData.displayedDate}</p>
                    <div className="BinSetInventoryChart-tooltipInfo-dataholder">
                      {tooltipData.lineIntercepts.map(({ value, colour, label }, index) => (
                        <div key={index} className="BinSetInventoryChart-tooltipInfo-dataholder-mass">
                          <div className="BinSetInventoryChart-tooltipCircle" style={{ backgroundColor: colour }} />
                          <span className="BinSetInventoryChart-tooltipInfo-dataholder-text">
                            {label ? `${label}: ` : ''}
                            {convertGramsToLargeUnits(isMetric, value)} {weightLargeUnitLabel(isMetric)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </TooltipWithBounds>
            )}
          </div>
        );
      }}
    </ParentSize>
  );
}

BinSetInventoryChart.propTypes = {
  step_bin_level_history: PropTypes.arrayOf(
    PropTypes.shape({
      t: PropTypes.number, // unix timestamp
      v: PropTypes.number, // value in grams
    }),
  ),
  projection_past_deliveries: PropTypes.arrayOf(
    PropTypes.shape({
      t: PropTypes.number,
      v: PropTypes.number,
    }),
  ),
  projection_with_scheduled: PropTypes.arrayOf(
    PropTypes.shape({
      t: PropTypes.number,
      v: PropTypes.number,
    }),
  ),
  projection_with_recommended: PropTypes.arrayOf(
    PropTypes.shape({
      t: PropTypes.number,
      v: PropTypes.number,
    }),
  ),
  chartStartTime: PropTypes.number, // unix timestamp
  chartEndTime: PropTypes.number, // unix timestamp
  isMetric: PropTypes.bool,
  chartHeight: PropTypes.number,
  chartMargin: PropTypes.shape({
    top: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number,
    right: PropTypes.number,
  }),
  knownBinLevelData: PropTypes.arrayOf(
    PropTypes.shape({
      valid_at: PropTypes.number,
      level_in_grams: PropTypes.number,
    }),
  ),
  displayTruthData: PropTypes.bool,
  totalBinCapacity: PropTypes.number,
  chartBackgroundBands: PropTypes.arrayOf(
    PropTypes.shape({
      tStart: PropTypes.number.isRequired,
      tEnd: PropTypes.number.isRequired,
      color: PropTypes.string.isRequired,
      opacity: PropTypes.number,
      label: PropTypes.string,
    }),
  ),
  eventMarkers: PropTypes.arrayOf(
    PropTypes.shape({
      t: PropTypes.number.isRequired,
      kind: PropTypes.oneOf(['open', 'close', 'vibration']).isRequired,
      interactionLabel: PropTypes.string,
      source: PropTypes.string,
      markerFill: PropTypes.string,
      label: PropTypes.string.isRequired,
      faultId: PropTypes.string,
      detail: PropTypes.string,
    }),
  ),
  chartHeading: PropTypes.string,
  chartHeaderAccessory: PropTypes.node,
  showProjectionLines: PropTypes.bool,
  fillParentHeight: PropTypes.bool,
  showLineLegend: PropTypes.bool,
  sharedZoomDomain: PropTypes.shape({
    start: PropTypes.oneOfType([PropTypes.number, PropTypes.instanceOf(Date)]),
    end: PropTypes.oneOfType([PropTypes.number, PropTypes.instanceOf(Date)]),
  }),
  onZoomDomainChange: PropTypes.func,
  hideXAxis: PropTypes.bool,
};

export default BinSetInventoryChart;
